import React, { useContext, useEffect, useState } from 'react';

import { pdf } from '@react-pdf/renderer';

import { Modal } from '@/components';
import AuthContext from '@/contexts/AuthContext';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { toast } from '@/design-system/toaster';

import { downloadBlob } from '../format';
import { useLoad } from '../hooks/useLoad';
import {
  buildPrintDocument,
  PRINT_TITLE,
  type PrintKind,
} from './buildPrintDocument';

/**
 * Preview-before-print for the WMS documents. The PDF is generated once in
 * the browser; the preview iframe, Cetak (hidden iframe → print dialog) and
 * Download PDF all use that same blob, so what ops see is what they print and
 * what they archive.
 */

export interface PrintTarget {
  kind: PrintKind;
  id: string;
}

interface Props {
  target: PrintTarget | null;
  onClose: () => void;
}

const WarehousePrintModal: React.FC<Props> = ({ target, onClose }) => {
  const auth = useContext(AuthContext);
  const printedBy = auth?.user?.displayName || auth?.user?.email || '';
  // Built once per target; the preview, Cetak and Download share that blob.
  const doc = useLoad(
    async (signal) => {
      const t = target as PrintTarget;
      const built = await buildPrintDocument(t.kind, t.id, printedBy, signal);
      const blob = await pdf(built.element).toBlob();
      return { blob, filename: built.filename, code: built.code };
    },
    [target, printedBy],
    !!target
  );
  const loading = doc.loading;
  const blob = loading ? null : (doc.data?.blob ?? null);
  const filename = doc.data?.filename ?? 'dokumen.pdf';
  const code = doc.data?.code ?? '';
  const error = doc.error?.message ?? null;

  // One object URL per blob, revoked when it is replaced or the modal closes.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);

  if (!target) return null;
  const title = PRINT_TITLE[target.kind];

  const handlePrint = () => {
    if (!blob) return;
    try {
      const printUrl = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
      iframe.src = printUrl;
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        // The print dialog is modal to the tab; removing the iframe too early
        // cancels the job.
        setTimeout(() => {
          iframe.remove();
          URL.revokeObjectURL(printUrl);
        }, 60_000);
      };
      document.body.appendChild(iframe);
    } catch (err) {
      toast.error('Gagal menyiapkan cetak', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      icon="ri-printer-line"
      title={`${title} — preview`}
      subtitle={code || undefined}
      width="full"
      className="max-w-5xl"
      padded={false}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button tone="neutral" style="stroke" onClick={onClose}>
            Tutup
          </Button>
          <Button
            tone="neutral"
            style="stroke"
            disabled={!blob}
            onClick={() => blob && downloadBlob(blob, filename)}
            leftIcon={<i className="ri-download-2-line text-[14px]" />}
          >
            Download PDF
          </Button>
          <Button
            disabled={!blob}
            onClick={handlePrint}
            leftIcon={<i className="ri-printer-line text-[14px]" />}
          >
            Cetak
          </Button>
        </div>
      }
    >
      <div className="bg-bg-weak-50 p-4">
        {loading && (
          <div
            className="flex h-[70vh] flex-col items-center justify-center gap-2 text-sm text-text-sub-600"
            role="status"
          >
            <i
              className="ri-loader-4-line animate-spin text-[22px]"
              aria-hidden
            />
            Menyiapkan dokumen…
          </div>
        )}
        {error && !loading && (
          <Alert
            status="error"
            appearance="stroke"
            size="lg"
            title="Dokumen tidak bisa dibuat"
            action={
              <Button
                tone="neutral"
                style="stroke"
                size="xs"
                onClick={doc.reload}
              >
                Coba lagi
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {url && !loading && (
          <iframe
            title={`Preview ${title}`}
            src={url}
            className="h-[70vh] w-full rounded-lg border border-stroke-soft-200 bg-bg-white-0"
          />
        )}
      </div>
    </Modal>
  );
};

export default WarehousePrintModal;
